import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, ArrowLeft, Loader2, Lock } from "lucide-react";
import { getRoom, joinRoom } from "@/lib/api";
import { generateEphemeralUsername, setSession } from "@/lib/session";

const AVATAR_COLORS = [
  "#A855F7",
  "#F472B6",
  "#38BDF8",
  "#34D399",
  "#FB923C",
  "#F87171",
  "#FACC15",
  "#818CF8",
];

export default function GuestJoin() {
  const { roomCode: paramCode } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState((paramCode || "").toUpperCase());
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [room, setRoom] = useState(null);
  const [color, setColor] = useState(AVATAR_COLORS[0]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  // Resolve room if param provided
  useEffect(() => {
    if (!paramCode) return;
    setChecking(true);
    getRoom(paramCode.toUpperCase())
      .then((r) => setRoom(r))
      .catch(() => toast.error("Room not found"))
      .finally(() => setChecking(false));
  }, [paramCode]);

  // Analytics: record page view for Join page if gtag available
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag('config', 'G-BX6PF1204M', { page_path: window.location.pathname });
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const checkCode = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const r = await getRoom(code.trim().toUpperCase());
      setRoom(r);
    } catch {
      toast.error("Room not found");
      setRoom(null);
    } finally {
      setChecking(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!nickname.trim() || !code.trim()) return;
    setLoading(true);
    try {
      const username = generateEphemeralUsername();
      const data = await joinRoom(code.trim().toUpperCase(), {
        nickname: nickname.trim(),
        avatar_color: color,
        password: room?.has_password ? password : null,
      });
      localStorage.setItem(
        `guest_${data.room_code}`,
        JSON.stringify({
          guest_id: data.guest_id,
          role: "guest",
          nickname: data.nickname,
          avatar_color: data.avatar_color,
          room_name: data.room_name,
          username,
        })
      );
      setSession({
        role: "guest",
        room_code: data.room_code,
        room_name: data.room_name,
        guest_id: data.guest_id,
        nickname: data.nickname,
        username,
      })
      toast.success(`Welcome, ${data.nickname}!`);
      navigate(`/guest/${data.room_code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to join");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ambient-bg min-h-screen relative">
      <div className="grain" />
      <div className="relative z-10 max-w-md mx-auto px-6 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-12"
          data-testid="back-to-landing"
        >
          <ArrowLeft size={18} /> Back
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-4xl font-bold text-white font-[Outfit] mb-2">
            Join a Room
          </h1>
          <p className="text-zinc-400 mb-10">
            Enter the 6-character code shown on the TV.
          </p>

          <form onSubmit={handleJoin} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Room Code
              </label>
              <input
                data-testid="room-code-input"
                type="text"
                className="input-base text-center text-2xl tracking-[0.4em] font-bold uppercase font-[Outfit]"
                placeholder="ABC123"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setRoom(null);
                }}
                onBlur={() => code.length >= 4 && !room && checkCode()}
                maxLength={8}
              />
              {checking && (
                <div className="text-xs text-zinc-500 mt-2 flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Checking...
                </div>
              )}
              {room && (
                <div className="text-xs text-emerald-400 mt-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  {room.room_name}
                </div>
              )}
            </div>

            {room?.has_password && (
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                  <Lock size={11} /> Room Password
                </label>
                <input
                  data-testid="room-password-input"
                  type="password"
                  className="input-base"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Your Nickname
              </label>
              <input
                data-testid="nickname-input"
                type="text"
                className="input-base"
                placeholder="Alex"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={40}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-3">
                Pick Your Color
              </label>
              <div className="flex flex-wrap gap-3">
                {AVATAR_COLORS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`avatar-color-${c.replace("#", "")}`}
                    className="w-10 h-10 rounded-full transition-all"
                    style={{
                      background: c,
                      boxShadow:
                        color === c
                          ? `0 0 0 3px #09090b, 0 0 0 5px ${c}`
                          : "none",
                      transform: color === c ? "scale(1.1)" : "scale(1)",
                    }}
                    aria-label={`Color ${c}`}
                  />
                ))}
              </div>
            </div>

            <button
              data-testid="join-room-submit"
              type="submit"
              disabled={
                loading ||
                !nickname.trim() ||
                !code.trim() ||
                (room?.has_password && !password)
              }
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Users size={18} />
              )}
              {loading ? "Joining..." : "Join Room"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
